<template>
  <div class="edi-settings tab-scroll-owner" v-loader.opacity="loading">
    <p class="description">{{ $t('edi.description') }}</p>

    <div class="section-selector">
      <button
        v-for="s in sections"
        :key="s.id"
        type="button"
        class="section-btn"
        :class="{active: activeSection === s.id}"
        :data-test="`btnSection-${s.id}`"
        @click="activeSection = s.id">
        {{ s.label }}
        <span class="count">{{ s.count }}</span>
      </button>
    </div>

    <div class="section-hint">
      {{ activeSectionHint }}
    </div>

    <div class="fields-table" v-if="pagedFields.length">
      <div class="fields-header">
        <div class="header-cell header-loop">{{ $t('clinicClaim.loopSegment') }}</div>
        <div class="header-cell header-element">{{ $t('clinicClaim.element') }}</div>
        <div class="header-cell header-description">{{ $t('clinicClaim.description') }}</div>
        <div class="header-cell header-value">{{ $t('clinicClaim.value') }}</div>
        <div class="header-cell header-actions"></div>
      </div>

      <EdiClaimFieldRow v-for="field in pagedFields" :key="field.id" :field="field" @change="handleFieldChange"/>
    </div>

    <!-- <div class="pagination-bar" v-if="totalPages > 1">
      <button class="btn btn-sm btn-outline-secondary" type="button" :disabled="currentPage === 1" @click="currentPage--">
        <i class="fa fa-chevron-left"></i>
      </button>

      <span class="page-info">{{ currentPage }} / {{ totalPages }}</span>

      <button class="btn btn-sm btn-outline-secondary" type="button" :disabled="currentPage === totalPages" @click="currentPage++">
        <i class="fa fa-chevron-right"></i>
      </button>
    </div> -->

    <div class="form-floating mt-3" v-if="activeSection !== 'patient' && isDataReady">
      <textarea class="form-control" id="notes" name="name" rows="3" v-model="notes"
                :placeholder="$t('clinicClaim.notesPlaceholder')" data-test="textareaNotes"></textarea>
      <label for="notes">{{ $t('clinicClaim.notesPlaceholder') }}</label>
    </div>

    <teleport to="#settingsActionBar" v-if="isDataReady">
      <div class="edi-action-buttons">
        <button class="btn btn-success" type="button" :disabled="loading || !dirty" @click="save" data-test="btnEdiSave">
          {{ $t('save') }}
        </button>

        <button class="btn btn-secondary" type="button" :disabled="loading" @click="openClaimPreviewOffcanvas" data-test="btnPreviewClaim">
          {{ $t('clinicClaim.generatePreview') }}
        </button>
      </div>
    </teleport>

    <teleport to="body">
      <EdiClaimPreviewOffcanvas :offcanvas_id="'EdiClaimPreviewOffcanvas'" ref="ediClaimPreviewOffcanvas"/>
    </teleport>
  </div>
</template>

<style lang="scss" scoped>
.edi-settings {
  padding:                       10px;

  .description {
    color:                       var(--Brand-Primary);
    font-size:                   var(--font-size-14);
    margin-bottom:               5px;
  }
}

.edi-action-buttons {
  display:                       flex;
  flex-direction:                row;
  align-items:                   center;
  gap:                           12px;
}

.section-selector {
  display:                       flex;
  margin-bottom:                 12px;
  border-bottom:                 1px solid var(--Shades-30-Border);

  .section-btn {
    @include                     transition(all 0.15s ease);
    color:                       var(--Shades-50);
    font-size:                   var(--font-size-12);;
    font-weight:                 500;
    letter-spacing:              2.4px;
    text-transform:              uppercase;
    cursor:                      pointer;
    display:                     flex;
    align-items:                 center;
    gap:                         8px;
    padding:                     15px;
    background:                  var(--Shades-BG);
    border:                      unset;
    border-bottom:               1px solid transparent;

    &:hover,
    &:active,
    &.active {
      color:                     var(--Support-Primary-Success);
      background:                var(--Shades-BG);
      border-bottom-color:       var(--Support-Primary-Success);
    }

    .count {
      @include                   border_radius(10px);
      color:                     var(--Shades-60-W);
      font-size:                 11px;
      padding:                   2px 6px;
      letter-spacing:            normal;
      background:                var(--Shades-20);
    }
  }
}

.section-hint {
  @include                       border_radius(4px);
  color:                         var(--Shades-60-W);
  font-size:                     12px;
  font-style:                    italic;
  padding:                       8px 12px;
  margin-bottom:                 16px;
  background:                    var(--Shades-10);
  border-left:                   3px solid var(--Brand-Secondary);
}

.fields-table {
  @include                       border_radius(6px);
  background:                    var(--Shades-BG);
  border:                        1px solid var(--Shades-20-Border);
  overflow:                      hidden;
}

.fields-header {
  display:                       grid;
  grid-template-columns:         150px 150px minmax(200px, 2fr) minmax(200px, 2fr) 90px;
  gap:                           8px;
  padding:                       8px 4px;
  background:                    var(--Shades-10);
  border-bottom:                 2px solid var(--Shades-20-Border);

  .header-cell {
    color:                       var(--Shades-60-W);
    font-size:                   11px;
    font-weight:                 600;
    text-transform:              uppercase;
    letter-spacing:              0.5px;
    padding:                     0 10px;
  }
}

@media (max-width: 991px) {
  .fields-header {
    grid-template-columns:       80px 90px 1fr 1fr 80px;
  }
}

.pagination-bar {
  display:                       flex;
  justify-content:               center;
  align-items:                   center;
  gap:                           12px;
  margin-top:                    12px;

  .page-info {
    color:                       var(--Brand-Primary);
    font-size:                   13px;
    text-align:                  center;
    min-width:                   60px;
  }
}
</style>

<script>
import { useUserStore } from '@/store/user'
import EdiClaimFieldRow from './EdiClaimFieldRow.vue'
import EdiClaimPreviewOffcanvas from './EdiClaimPreviewOffcanvas.vue'

const PAGE_SIZE = 20 // 15

export default {
  name: 'EdiSettings',
  components: {
    EdiClaimFieldRow,
    EdiClaimPreviewOffcanvas
  },
  data: () => ({
    loading: false,
    isDataReady: false,
    session: null,
    clinicId: null,
    fields: [],
    overrides: {},
    notes: '',
    dirty: false,
    activeSection: 'default',
    currentPage: 1,
  }),
  watch: {
    activeSection() {
      this.currentPage = 1
    },
    notes() {
      if (this.isDataReady) this.dirty = true
    }
  },
  computed: {
    sections() {
      const counts = this.fields.reduce((acc, f) => {
        acc[f.category] = (acc[f.category] || 0) + 1
        return acc
      }, {})
      return [
        {id: 'default',   label: this.$t('clinicClaim.sectionDefault'),   count: counts.default || 0},
        {id: 'physician', label: this.$t('clinicClaim.sectionPhysician'), count: counts.physician || 0},
        {id: 'patient',   label: this.$t('clinicClaim.sectionPatient'),   count: counts.patient || 0}
      ]
    },
    activeSectionHint() {
      switch (this.activeSection) {
        case 'default':   return this.$t('clinicClaim.sectionDefaultHint')
        case 'physician': return this.$t('clinicClaim.sectionPhysicianHint')
        case 'patient':   return this.$t('clinicClaim.sectionPatientHint')
        default: return ''
      }
    },
    sectionFields() {
      return this.fields.filter(f => f.category === this.activeSection)
    },
    totalPages() {
      return Math.max(1, Math.ceil(this.sectionFields.length / PAGE_SIZE))
    },
    pagedFields() {
      const start = (this.currentPage - 1) * PAGE_SIZE
      return this.sectionFields.slice(start, start + PAGE_SIZE)
    },
  },
  mounted() {
    const userStore = useUserStore()
    this.session = userStore.sessionInfo
    this.clinicId = this.session.currentClinic && this.session.currentClinic.id

    if (this.clinicId)
      this.fetchData()
  },
  methods: {
    async fetchData() {
      this.loading = true
      try {
        const data = await this.$claimConfigService.get({clinicId: this.clinicId})
        this.fields = data.fields || []
        this.notes = data.notes || ''
        this.overrides = this.fields.reduce((acc, f) => {
          if (f.isOverridden) acc[f.id] = f.currentValue
          return acc
        }, {})
        this.dirty = false
        this.isDataReady = true
      } catch (err) {
        this.$message.popup(this, this.$i18n.t('clinicClaim.loadError'), 'operationFail', 'error')
      } finally {
        this.loading = false
      }
    },
    handleFieldChange({id, value}) {
      const field = this.fields.find(f => f.id === id)
      if (!field || !field.editable) return

      if (value === '' || value == null) {
        delete this.overrides[id]
        field.currentValue = field.defaultValue
        field.isOverridden = false
      } else {
        this.overrides[id] = value
        field.currentValue = value
        field.isOverridden = true
      }
      this.dirty = true
    },
    async save() {
      this.loading = true
      try {
        await this.$claimConfigService.save({clinicId: this.clinicId, overrides: this.overrides, notes: this.notes})
        this.$message.popup(this, this.$i18n.t('clinicClaim.saveSuccess'), 'operationSuccess', 'success')
        this.dirty = false
      } catch (err) {
        this.$message.popup(this, this.$i18n.t('clinicClaim.saveError'), 'operationFail', 'error')
      } finally {
        this.loading = false
      }
    },
    async openClaimPreviewOffcanvas() {
      const ok = await this.$refs.ediClaimPreviewOffcanvas.show({
        clinicID: this.clinicId,
        overrides: this.overrides,
      })
      if (ok) {
        // this.fetchData()
      }
    }
  }
}
</script>
